import { Box, Grid, Typography, Slider, Button } from "@mui/material";
import React, { useState, useEffect } from "react";
import { Maps, LoadingSpinner } from "@components";
import moment from "moment";

const RouteHistoryMap = (props) => {
	const { routeData, truckData, startDate, endDate, loading } = props;

	// SLIDER STATE
	const [sliderTimestamp, setSliderTimestamp] = useState(null);
	const [displayedRoute, setDisplayedRoute] = useState(null); // The Filtered Data for Map
	const [isPlaying, setIsPlaying] = useState(false);

	const minTime = moment(startDate).startOf('day').unix();
	const maxTime = moment(endDate).endOf('day').unix();
	const isMultiDay = moment(endDate).diff(moment(startDate), 'days') > 0;

	// When new data arrives, reset slider to end
	useEffect(() => {
		if (routeData && Array.isArray(routeData) && routeData.length > 0) {
			const lastRecordTs = parseInt(routeData[routeData.length - 1].timestamp);
			// Default to the actual last data point available in the trace
			setSliderTimestamp(lastRecordTs || maxTime);
		} else {
			setDisplayedRoute(null);
		}
	}, [routeData, startDate, endDate]);

	// Filter route when slider or data changes
	useEffect(() => {
		if (!routeData || !Array.isArray(routeData)) {
			setDisplayedRoute(null);
			return;
		}

		if (sliderTimestamp === null) {
			setDisplayedRoute(routeData);
			return;
		}

		// FILTER LOGIC
		const filtered = routeData.filter(point => parseInt(point.timestamp) <= sliderTimestamp);

		if (filtered.length > 0) {
			const lastPoint = filtered[filtered.length - 1];
			// Attach metadata for the map's truck indicator
			filtered.direction = lastPoint.direction || 0;
			filtered.lat = lastPoint.lat;
			filtered.lng = lastPoint.lng;
			filtered.speed = lastPoint.speed;
			filtered.timestamp = lastPoint.timestamp;

			setDisplayedRoute(filtered);
		} else {
			setDisplayedRoute(null);
		}
	}, [sliderTimestamp, routeData]);

	// PLAYBACK EFFECT
	useEffect(() => {
		let interval;
		if (isPlaying && routeData && routeData.length > 0) {
			const effectiveMax = parseInt(routeData[routeData.length - 1].timestamp);
			const effectiveMin = parseInt(routeData[0].timestamp);
			
			// Adaptive step size: cover the whole range in roughly 30-60 seconds
			const rangeSize = effectiveMax - effectiveMin;
			const stepSize = Math.max(60, Math.floor(rangeSize / 300)); // At least 1 min, but scale up for long ranges

			interval = setInterval(() => {
				setSliderTimestamp((prev) => {
					const current = prev === null ? effectiveMin : prev;
					const nextVal = current + stepSize; 
					if (nextVal >= effectiveMax) {
						setIsPlaying(false);
						return effectiveMax;
					}
					return nextVal;
				});
			}, 100);
		}
		return () => clearInterval(interval);
	}, [isPlaying, routeData]);

	const handleSliderChange = (event, newValue) => {
		setSliderTimestamp(newValue);
	};

	const handlePlayPause = () => {
		setIsPlaying(!isPlaying);
	};

	const handleReset = () => {
		setSliderTimestamp(minTime);
		setIsPlaying(false);
	};

	const formatTimeLabel = (unix) => {
		return moment.unix(unix).format(isMultiDay ? "DD MMM HH:mm" : "hh:mm A");
	};

	if (loading) return <LoadingSpinner />;
	
	// Validation for route data content (coordinates check)
	const hasValidCoords = routeData && routeData.some(p => p.lat !== 0 && p.lng !== 0);

	if (!routeData || routeData.length === 0) return (
		<Box sx={{ p: 10, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: 4, border: '2px dashed #cbd5e1' }}>
			<Typography variant="h6" color="textSecondary" fontWeight={700}>NO ROUTE DATA FOUND</Typography>
			<Typography variant="body2" color="textSecondary">Selected vehicle has no recorded movement for this period.</Typography>
		</Box>
	);

	return (
		<Grid container spacing={2}>
			{/* TIME SLIDER SECTION */}
			<Grid item xs={12} sx={{ px: 2, py: 1 }}>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 2.5,
						p: 2.5,
						borderRadius: 3,
						background: 'linear-gradient(135deg, #ffffff 0%, #f1f5ff 100%)',
						border: '1px solid rgba(37, 99, 235, 0.12)',
						boxShadow: '0 4px 20px rgba(37, 99, 235, 0.06), inset 0 1px 0 rgba(255,255,255,0.8)',
					}}
				>
					<Box sx={{ textAlign: 'center', minWidth: 72 }}>
						<Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', letterSpacing: 1.2, fontSize: 11, display: 'block' }}>START</Typography>
						<Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontVariantNumeric: 'tabular-nums' }}>
							{moment.unix(minTime).format(isMultiDay ? "DD/MM" : "HH:mm")}
						</Typography>
					</Box>

					<Slider
						value={sliderTimestamp || maxTime}
						min={minTime}
						max={maxTime}
						step={60}
						onChange={handleSliderChange}
						valueLabelDisplay="auto"
						valueLabelFormat={formatTimeLabel}
						sx={{
							flexGrow: 1,
							height: 6,
							'& .MuiSlider-rail': { opacity: 0.35, background: 'linear-gradient(90deg, #cbd5e1, #e2e8f0)' },
							'& .MuiSlider-track': { border: 'none', background: 'linear-gradient(90deg, #3b82f6, #2563eb)' },
							'& .MuiSlider-thumb': {
								width: 22,
								height: 22,
								background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
								boxShadow: '0 2px 8px rgba(37, 99, 235, 0.4)',
								border: '3px solid #ffffff',
								'&:hover, &.Mui-focusVisible': { boxShadow: '0 0 0 8px rgba(37, 99, 235, 0.16)' },
							},
							'& .MuiSlider-valueLabel': {
								background: '#1e293b',
								borderRadius: '8px',
								fontWeight: 600,
								fontSize: 12,
							},
						}}
					/>

					<Box sx={{ textAlign: 'center', minWidth: 72 }}>
						<Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', letterSpacing: 1.2, fontSize: 11, display: 'block' }}>END</Typography>
						<Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontVariantNumeric: 'tabular-nums' }}>
							{moment.unix(maxTime).format(isMultiDay ? "DD/MM" : "HH:mm")}
						</Typography>
					</Box>

					<Button
						variant="contained"
						onClick={handlePlayPause}
						disableElevation
						startIcon={
							<Box
								component="svg"
								viewBox="0 0 24 24"
								sx={{ width: 18, height: 18, fill: 'currentColor' }}
							>
								{isPlaying ? (
									<>
										<rect x="6" y="5" width="4" height="14" rx="1.2" />
										<rect x="14" y="5" width="4" height="14" rx="1.2" />
									</>
								) : (
									<path d="M8 5.14v13.72a1 1 0 0 0 1.54.84l10.29-6.86a1 1 0 0 0 0-1.68L9.54 4.3A1 1 0 0 0 8 5.14z" />
								)}
							</Box>
						}
						sx={{
							minWidth: 132,
							height: 44,
							px: 2.5,
							borderRadius: '999px',
							fontWeight: 700,
							fontSize: 13,
							letterSpacing: 0.5,
							textTransform: 'none',
							color: '#fff',
							background: isPlaying
								? 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)'
								: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
							boxShadow: isPlaying
								? '0 6px 20px rgba(234, 88, 12, 0.35)'
								: '0 6px 20px rgba(37, 99, 235, 0.35)',
							transition: 'all 0.25s ease',
							'&:hover': {
								transform: 'translateY(-1px)',
								background: isPlaying
									? 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)'
									: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
								boxShadow: isPlaying
									? '0 8px 24px rgba(234, 88, 12, 0.45)'
									: '0 8px 24px rgba(37, 99, 235, 0.45)',
							},
							'&:active': { transform: 'translateY(0)' },
						}}
					>
						{isPlaying ? 'PAUSE' : 'PLAY ROUTE'}
					</Button>

					<Button
						variant="outlined"
						onClick={handleReset}
						sx={{
							height: 44,
							px: 2.5,
							borderRadius: '999px',
							fontWeight: 700,
							fontSize: 13,
							letterSpacing: 0.5,
							textTransform: 'none',
							borderColor: 'rgba(37, 99, 235, 0.3)',
							color: '#2563eb',
							background: 'rgba(255,255,255,0.6)',
							backdropFilter: 'blur(4px)',
							transition: 'all 0.25s ease',
							'&:hover': {
								borderColor: '#2563eb',
								background: 'rgba(37, 99, 235, 0.06)',
								transform: 'translateY(-1px)',
							},
						}}
					>
						RESET
					</Button>
				</Box>

				<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
					<Box
						sx={{
							display: 'inline-flex',
							alignItems: 'center',
							gap: 0.75,
							px: 1.5,
							py: 0.5,
							borderRadius: '999px',
							background: 'rgba(100, 116, 139, 0.08)',
							color: '#475569',
							fontSize: 12,
							fontWeight: 600,
						}}
					>
						<Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#64748b' }} />
						Total Points: <strong style={{ color: '#1e293b' }}>{routeData.length}</strong>
					</Box>
					<Box
						sx={{
							display: 'inline-flex',
							alignItems: 'center',
							gap: 0.75,
							px: 1.75,
							py: 0.5,
							borderRadius: '999px',
							background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.12), rgba(37, 99, 235, 0.08))',
							color: '#1d4ed8',
							fontSize: 12.5,
							fontWeight: 700,
							fontVariantNumeric: 'tabular-nums',
						}}
					>
						<Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#2563eb', boxShadow: '0 0 0 3px rgba(37,99,235,0.2)' }} />
						Playback Time: {sliderTimestamp ? formatTimeLabel(sliderTimestamp) : formatTimeLabel(maxTime)}
					</Box>
					{!hasValidCoords && (
						<Box
							sx={{
								display: 'inline-flex',
								alignItems: 'center',
								gap: 0.75,
								px: 1.5,
								py: 0.5,
								borderRadius: '999px',
								background: 'rgba(239, 68, 68, 0.1)',
								color: '#b91c1c',
								fontSize: 12,
								fontWeight: 700,
							}}
						>
							⚠ GPS positioning unavailable (0,0)
						</Box>
					)}
				</Box>
			</Grid>

			<Grid item xs={12}>
				<Box sx={{ height: 500, borderRadius: 4, overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}>
					<Maps
						data={truckData}
						polygonLine={displayedRoute || routeData}
						id={moment().unix()}
					/>
				</Box>
			</Grid>
		</Grid>
	);
};

export default RouteHistoryMap;
